import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { TopBar } from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";

export function Login() {
  const { login, error } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [coachExists, setCoachExists] = useState(true);

  React.useEffect(() => {
    import("@/lib/api").then(({ api }) => {
      api.get("/auth/setup-status").then(r => setCoachExists(r.data.coach_exists)).catch(()=>{});
    });
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      const dest = loc.state?.from || (u.role === "coach" ? "/coach" : (u.onboarding_complete ? "/app/dashboard" : "/onboarding"));
      nav(dest, { replace: true });
    } catch { /* handled */ }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar />
      <div className="max-w-md mx-auto px-5 py-16">
        <h1 className="font-display font-black uppercase text-4xl tracking-tighter">Welcome back</h1>
        <p className="text-neutral-400 mt-2">Keep the discipline. Keep the data.</p>
        {!coachExists && (
          <div className="mt-4 kw-card p-3 border-gold" data-testid="setup-needed-banner">
            <div className="text-[10px] uppercase tracking-widest text-gold">Coach not activated yet</div>
            <div className="text-sm text-neutral-300 mt-1">Your private coach account hasn't been created. Finish the one-time secure setup first.</div>
            <Link to="/setup-coach" className="kw-btn-primary inline-block mt-3 text-xs" data-testid="goto-setup">Activate my coach account</Link>
          </div>
        )}
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <label className="kw-label">Email</label>
            <input className="kw-input" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="login-email"/>
          </div>
          <div>
            <label className="kw-label">Password</label>
            <input className="kw-input" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="login-password"/>
          </div>
          {error && <div className="text-red-400 text-sm" data-testid="login-error">{error}</div>}
          <button className="kw-btn-primary w-full" disabled={loading} data-testid="login-submit">{loading? "Signing in…" : "Sign In"}</button>
        </form>
        <p className="mt-6 text-sm text-neutral-400">
          New client? <Link to="/apply" className="text-gold uppercase tracking-widest text-xs" data-testid="to-register">Apply for coaching</Link>
        </p>
      </div>
    </div>
  );
}

export function Register() {
  const { register, error } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({...form, [k]: e.target.value});

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      nav(u.role === "coach" ? "/coach" : "/onboarding", { replace: true });
    } catch { /* handled */ }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <TopBar />
      <div className="max-w-md mx-auto px-5 py-12">
        <h1 className="font-display font-black uppercase text-4xl tracking-tighter">Create your account</h1>
        <p className="text-neutral-400 mt-2">Start your 12-week transformation.</p>
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div><label className="kw-label">Full name</label><input className="kw-input" value={form.name} onChange={update("name")} required data-testid="reg-name"/></div>
          <div><label className="kw-label">Email</label><input className="kw-input" type="email" value={form.email} onChange={update("email")} required data-testid="reg-email"/></div>
          <div><label className="kw-label">Password (min 8)</label><input className="kw-input" type="password" minLength={8} value={form.password} onChange={update("password")} required data-testid="reg-password"/></div>
          {error && <div className="text-red-400 text-sm" data-testid="reg-error">{error}</div>}
          <button className="kw-btn-primary w-full" disabled={loading} data-testid="reg-submit">{loading? "Creating…":"Create Account"}</button>
        </form>
        <p className="mt-6 text-sm text-neutral-400">Already a warrior? <Link to="/login" className="text-gold uppercase tracking-widest text-xs" data-testid="to-login">Sign in</Link></p>
      </div>
    </div>
  );
}
